import React, { useState } from 'react';
import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import withLayout from '../../hocs/Layout';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShieldIcon from '@mui/icons-material/Shield';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PageTitle from '../../common/PageTitle';
import MUIModal from '../../common/MUIModal';
import Loader from '../../common/Loader';
import KpiCard from './shared/KpiCard';
import FilterBar from './shared/FilterBar';
import { getDefaultDateRange, buildPillFilter, PILL_KEYS } from './shared/constants';
import useAppointmentSearch from './shared/useAppointmentSearch';
import useAppointmentDashboard from './shared/useAppointmentDashboard';
import { buildAppointmentColumns } from './shared/columns';
import ExpandableAppointmentsTable from './shared/ExpandableAppointmentsTable';
import ExportButton from './shared/ExportButton';
import PageBanner from './shared/PageBanner';
import AppointmentDelivery from '../orders/list2/AddAppDelivery';

const SECTION_FILTERS = {
    needsScheduling: buildPillFilter(PILL_KEYS.NEEDS_SCHEDULING),
    escalation3plus: buildPillFilter(PILL_KEYS.MISS_3_PLUS),
    prozoFaultPending: buildPillFilter(PILL_KEYS.PROZO_FAULT_PENDING),
};

function Section({ title, count, color, intro, rows, rowCount, isFetching, columns, page, setPage, sizePerPage, setSizePerPage, exportFilters, exportExtraAnd, exportFileName }) {
    return (
        <Card sx={{ mb: 3, borderLeft: `4px solid ${color}` }}>
            <CardContent>
                <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
                    <Typography variant='subtitle1' fontWeight={700}>
                        {title}{' '}
                        <Box component='span' sx={{ ml: 1, bgcolor: color, color: '#fff', fontSize: 11, px: 1.2, py: 0.3, borderRadius: 5, fontWeight: 700, verticalAlign: 'middle' }}>
                            {count ?? '—'}
                        </Box>
                    </Typography>
                    {exportFilters && (
                        <ExportButton label='Export' filters={exportFilters} extraAndFilter={exportExtraAnd} fileName={exportFileName} />
                    )}
                </Stack>
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1.5 }}>{intro}</Typography>
                {isFetching && <Loader />}
                <ExpandableAppointmentsTable
                    columns={columns}
                    rows={rows}
                    page={page}
                    sizePerPage={sizePerPage}
                    setPage={setPage}
                    setSizePerPage={setSizePerPage}
                    rowCount={rowCount ?? -1}
                    getRowId={(row) => row.id}
                />
            </CardContent>
        </Card>
    );
}

function NeedsAction({ abd = true }) {
    const def = getDefaultDateRange();
    const [filters, setFilters] = useState({ fromDate: def.fromDate, toDate: def.toDate });
    const [reload, setReload] = useState(0);
    const [scheduleRow, setScheduleRow] = useState(null);

    const [page1, setPage1] = useState(0); const [size1, setSize1] = useState(20);
    const [page2, setPage2] = useState(0); const [size2, setSize2] = useState(20);
    const [page3, setPage3] = useState(0); const [size3, setSize3] = useState(20);

    const sec1 = useAppointmentSearch({ filters, extraAndFilter: SECTION_FILTERS.needsScheduling, page: page1, sizePerPage: size1, reload, appointmentRequired: abd });
    const sec2 = useAppointmentSearch({ filters, extraAndFilter: SECTION_FILTERS.escalation3plus, page: page2, sizePerPage: size2, reload, appointmentRequired: abd });
    const sec3 = useAppointmentSearch({ filters, extraAndFilter: SECTION_FILTERS.prozoFaultPending, page: page3, sizePerPage: size3, reload, appointmentRequired: abd });
    const { data: kpi } = useAppointmentDashboard({ filters, reload, include: ['buckets', 'summary'], appointmentRequired: abd });

    const triggerReload = () => setReload((r) => r + 1);

    const columns = buildAppointmentColumns({ onSchedule: (row) => setScheduleRow(row), showCloseButton: false });

    return (
        <div id='main-content' className='container-fluid'>
            <PageTitle>Needs Action</PageTitle>

            <PageBanner variant='danger' icon={<ErrorOutlineIcon />} title='Your Action Items — What Needs Your Attention Right Now'>
                Shipments that need <strong>immediate action</strong>: schedule a new appointment or escalate 3+ misses. Work top to bottom.
            </PageBanner>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='red' label='Needs Scheduling' value={kpi.needsScheduling}
                        sub='No appointment booked yet' icon={<ErrorOutlineIcon />} highlightValue borderColor='#c62828' />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='amber' label='3+ Misses (Escalation)' value={kpi.miss3plus}
                        sub='Requires review' icon={<WarningAmberIcon />} />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='red' label='Avg Days Pending' value={kpi.avgDaysPending}
                        sub='Since last missed appointment' icon={<AccessTimeIcon />} />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='amber' label='Prozo Fault (Pending)' value={kpi.prozoFaultPending}
                        sub='Prozo-caused, still unscheduled' icon={<ShieldIcon />} />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='green' label='Resolved Today' value={kpi.resolvedToday}
                        sub='Scheduled or delivered today' icon={<CheckCircleIcon />} />
                </Grid>
            </Grid>

            <FilterBar
                show={['courier', 'awb', 'city', 'dateRange']}
                onApply={(f) => { setFilters(f); triggerReload(); }}
                onReset={() => { setFilters({ fromDate: def.fromDate, toDate: def.toDate }); triggerReload(); }}
            />

            <Section
                title='Step 1: Schedule New Appointments'
                count={kpi.needsScheduling}
                color='#c62828'
                intro='Shipments with last appointment missed and no new appointment booked. Click Schedule.'
                rows={sec1.data?.data || []}
                rowCount={sec1.data?.totalRecords ?? -1}
                isFetching={sec1.isFetching}
                columns={columns}
                page={page1} setPage={setPage1} sizePerPage={size1} setSizePerPage={setSize1}
                exportFilters={filters}
                exportExtraAnd={SECTION_FILTERS.needsScheduling}
                exportFileName='ABD_Needs_Scheduling'
            />

            <Section
                title='Step 2: Escalate High Miss Count (3+ Misses)'
                count={kpi.miss3plus}
                color='#e65100'
                intro='Shipments that have failed 3 or more times. Review and escalate to ops if needed.'
                rows={sec2.data?.data || []}
                rowCount={sec2.data?.totalRecords ?? -1}
                isFetching={sec2.isFetching}
                columns={columns}
                page={page2} setPage={setPage2} sizePerPage={size2} setSizePerPage={setSize2}
                exportFilters={filters}
                exportExtraAnd={SECTION_FILTERS.escalation3plus}
                exportFileName='ABD_Escalation_3plus_Misses'
            />

            <Section
                title='Step 3: Review Prozo-Caused Pending Items'
                count={kpi.prozoFaultPending}
                color='#5c6bc0'
                intro='Missed due to Prozo and still need new appointment. Prioritize to maintain service levels.'
                rows={sec3.data?.data || []}
                rowCount={sec3.data?.totalRecords ?? -1}
                isFetching={sec3.isFetching}
                columns={columns}
                page={page3} setPage={setPage3} sizePerPage={size3} setSizePerPage={setSize3}
                exportFilters={filters}
                exportExtraAnd={SECTION_FILTERS.prozoFaultPending}
                exportFileName='ABD_Prozo_Fault_Pending'
            />

            <MUIModal
                open={!!scheduleRow}
                handleClose={() => setScheduleRow(null)}
                maxWidth='lg'
                hideClose
                title={
                    <Typography variant='subtitle1'>
                        Schedule Appointment — AWB:{' '}
                        <Typography component='span' color='error' fontWeight='bold'>{scheduleRow?.awb_number}</Typography>
                    </Typography>
                }
            >
                <AppointmentDelivery rowData={scheduleRow} setDelConfirm={() => setScheduleRow(null)} setReload={triggerReload} />
            </MUIModal>
        </div>
    );
}

export default withLayout(NeedsAction);
